import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { getReportFooter, getReportHeader } from "@/reports/ReportUtil";
import { styles } from "@/reports/reportStyles";
import useCoordinates from "@/composables/coordinates.js";
import { convertToInt } from "@/composables/util";
import { getFormattedDateStringNoTime } from "@/composables/dateField.js";
import { CUSTOM_ICONS, SYMBOLS } from "@/constants/Unicode";
import { sortStructure } from "@/util/sortStructure";

function setGeneralDocDefinition(structure) {
  const configStore = useConfigStore();
  const coordinates = useCoordinates();
  const spanDetails = [];
  spanDetails.push(getDetailsHeader());
  sortStructure(structure, "structureUnits", "strUnitKey");
  getSpecificSpanDetails(structure, spanDetails, configStore);

  let docDefinition = {
    header: function () {
      return getReportHeader("General Page", structure);
    },
    footer: function (currentPage, pageCount) {
      return getReportFooter(currentPage, pageCount);
    },
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "General Page" },
      {
        columns: [
          [
            {
              table: {
                headerRows: 1,
                widths: ["45%", "*"],
                body: [
                  [
                    {
                      colSpan: 2,
                      text: "Structure Identification",
                      fillColor: "#ebebeb",
                      alignment: "center",
                      style: ["sectionHeader"],
                    },
                    {},
                  ],
                  [
                    {
                      text: "5A01 BMS ID:",
                      bold: true,
                    },
                    {
                      text: structure.structureBridge?.bmsId,
                    },
                  ],
                  [
                    {
                      text: "5A02 Structure Name:",
                      bold: true,
                    },
                    {
                      text: structure.structureBridge?.strucName,
                    },
                  ],
                  [
                    {
                      text: "5A03 NBI Structure No:",
                      bold: true,
                    },
                    {
                      text: structure.structureBridge?.strucNum,
                    },
                  ],
                ],
              },
              layout: "noBorders",
            },
            {
              table: {
                headerRows: 1,
                widths: ["45%", "*"],
                body: [
                  [
                    {
                      colSpan: 2,
                      text: "Location",
                      fillColor: "#ebebeb",
                      alignment: "center",
                      style: ["sectionHeader"],
                    },
                    {},
                  ],
                  [
                    {
                      text: "5A04 District:",
                      bold: true,
                    },
                    {
                      text: getDistrict(configStore, structure.structureBridge?.district),
                    },
                  ],
                  [
                    {
                      text: "5A05 County:",
                      bold: true,
                    },
                    {
                      text: getCounty(configStore, structure.structureBridge?.countyFIPSCode),
                    },
                  ],
                  [
                    {
                      text: "5A06 City/Town/Place:",
                      bold: true,
                    },
                    {
                      text: getMunicipality(configStore, structure.structureBridge?.placeCode),
                    },
                  ],
                  [
                    {
                      text: "5A07 Feature Intersected:",
                      bold: true,
                    },
                    {
                      text: structure.structureBridge?.featInt,
                    },
                  ],
                  [
                    {
                      text: "5A08 Facility Carried:",
                      bold: true,
                    },
                    {
                      text: structure.structureBridge?.facility,
                    },
                  ],
                  [
                    {
                      text: "5A09 Location:",
                      bold: true,
                    },
                    {
                      text: structure.structureBridge?.location,
                    },
                  ],
                  [
                    {
                      text: "5A10 Latitude:",
                      bold: true,
                    },
                    {
                      text: coordinates.parseBMSLatLongToDMS(structure.structureBridge?.latitude, "N").decimal,
                    },
                  ],
                  [
                    {
                      text: "5A11 Longitude:",
                      bold: true,
                    },
                    {
                      text: coordinates.parseBMSLatLongToDMS(structure.structureBridge?.longitude, "W").decimal,
                    },
                  ],
                ],
              },
              layout: "noBorders",
            },
            {
              table: {
                headerRows: 1,
                widths: ["45%", "*"],
                body: [
                  [
                    {
                      colSpan: 2,
                      text: "Age and Service",
                      fillColor: "#ebebeb",
                      alignment: "center",
                      style: ["sectionHeader"],
                    },
                    {},
                  ],
                  [
                    {
                      text: "5A15 Year Built:",
                      bold: true,
                    },
                    {
                      text: structure?.structureBridge?.yearBuilt,
                    },
                  ],
                  [
                    {
                      text: "5A16 Year Reconstruction:",
                      bold: true,
                    },
                    {
                      text: structure?.structureBridge?.yearRecon,
                    },
                  ],
                  [
                    {
                      text: "5A17 Service Type On:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.SERVICE_TYPE_ON,
                        structure?.structureBridge?.servTypOn,
                      ),
                    },
                  ],
                  [
                    {
                      text: "5A18 Service Type Under:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.SERVICE_TYPE_UNDER,
                        structure?.structureBridge?.servTypUnd,
                      ),
                    },
                  ],
                  [
                    {
                      text: "5A19 # of Lanes Under:",
                      bold: true,
                    },
                    {
                      text: structure?.structureBridge?.sumLanes,
                    },
                  ],
                  [
                    {
                      text: "5A20 Maint Resp:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.MAINTENENCE_RESPONSIBILITY,
                        structure?.structureBridge?.custodian,
                      ),
                    },
                  ],
                  [
                    {
                      text: "5A21 Owner Code:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(REFERENCE_TABLE.OWNER, structure?.structureBridge?.owner),
                    },
                  ],
                  [
                    {
                      text: "6A06 Submitting Agency:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(REFERENCE_TABLE.SUB_AGENCY, structure?.userBridge?.subAgency),
                    },
                  ],
                  [
                    {
                      text: "6A23 Owner Description:",
                      bold: true,
                    },
                    {
                      text: structure?.userBridge?.ownerDesc,
                    },
                  ],
                  [
                    {
                      text: "6A19 BPN:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.BUS_PLAN_NETWORK,
                        structure?.userBridge?.busPlanNetwork,
                      ),
                    },
                  ],
                  [
                    {
                      text: "VP02 Posting Status:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.POSTING_STATUS,
                        structure?.postings?.find((posting) => posting.active == "1")?.postStatus,
                      ),
                    },
                  ],
                ],
              },
              layout: "noBorders",
            },
          ],
          [
            {
              table: {
                headerRows: 1,
                widths: ["52%", "*"],
                body: [
                  [
                    {
                      colSpan: 2,
                      text: "Deck Information",
                      fillColor: "#ebebeb",
                      alignment: "center",
                      style: ["sectionHeader"],
                    },
                    {},
                  ],
                  [
                    { text: "5B01 Deck Structure Type:", bold: true },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.DECK_STRUCTURE_TYPE,
                        structure.structureBridge?.dkStrucType,
                      ),
                    },
                  ],
                  [
                    { text: "5B02 Deck Surface Type:", bold: true },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.DECK_SURFACE_TYPE,
                        structure.structureBridge?.dkSurfType,
                      ),
                    },
                  ],
                  [
                    { text: "5B03 Deck Membrane Type:", bold: true },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.DECK_MEMBRANE_TYPE,
                        structure.structureBridge?.dkMembType,
                      ),
                    },
                  ],
                  [
                    { text: "5B04 Deck Protection:", bold: true },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.DECK_PROTECTION_TYPE,
                        structure.structureBridge?.dkProtect,
                      ),
                    },
                  ],
                  [
                    { text: "5B05 Left Curb/S'walk Width:", bold: true },
                    {
                      text: structure.structureBridge?.lftCurbSw,
                    },
                  ],
                  [
                    { text: "5B06 Right Curb/S'walk Width:", bold: true },
                    {
                      text: structure.structureBridge?.rtCurbSw,
                    },
                  ],
                  [
                    { text: "5B07 Deck Width (O/O):", bold: true },
                    {
                      text: structure.structureBridge?.deckWidth,
                    },
                  ],
                  [
                    { text: "5B08 Median Type:", bold: true },
                    {
                      text: structure.structureBridge?.bridgeMed,
                    },
                  ],
                  [
                    { text: "5B09 Skew:", bold: true },
                    {
                      text: structure.structureBridge?.skew ? structure.structureBridge?.skew + SYMBOLS.DEGREE : "",
                    },
                  ],
                  [
                    { text: "5B10 Structure Flare:", bold: true },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.STRUCTURE_FLARED,
                        structure.structureBridge?.strFlared,
                      ),
                    },
                  ],
                  [
                    { text: "6A38 PennDOT Deck Type:", bold: true },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.BRIDGE_DECK_TYPE,
                        structure?.userBridge?.deptDKStruTyp,
                      ),
                    },
                  ],
                  [
                    { text: "6A39 Relief Joints?:", bold: true },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.RELIEF_JOINT,
                        structure?.userBridge?.reliefJoint,
                      ),
                    },
                  ],
                  [
                    { text: "6A40 Form Type:", bold: true },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.DECK_FORM_TYPE,
                        structure?.userBridge?.deckFormType,
                      ),
                    },
                  ],
                  [
                    { text: "6A41 No. of Joints:", bold: true },
                    {
                      text: structure?.userBridge?.deckJoints,
                    },
                  ],
                ],
              },
              layout: "noBorders",
            },
            {
              table: {
                headerRows: 1,
                widths: ["52%", "*"],
                body: [
                  [
                    {
                      colSpan: 2,
                      text: "Span Information",
                      fillColor: "#ebebeb",
                      alignment: "center",
                      style: ["sectionHeader"],
                    },
                    {},
                  ],
                  [
                    {
                      text: "5B11 No. of Main Spans:",
                      bold: true,
                    },
                    {
                      text: structure?.structureBridge?.mainSpans,
                    },
                  ],
                  [
                    {
                      text: "5B12 Main Span Mat'l Type:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.MAIN_SPAN_MATERIAL,
                        structure?.structureBridge?.materialMain,
                      ),
                    },
                  ],
                  [
                    {
                      text: "5B13 Main Span Design Type:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.MAIN_SPAN_DESIGN,
                        structure?.structureBridge?.designMain,
                      ),
                    },
                  ],
                  [
                    {
                      text: "5B14 No. of Approach Spans:",
                      bold: true,
                    },
                    {
                      text: structure.structureBridge?.appSpans,
                    },
                  ],
                  [
                    {
                      text: "5B15 Appr Span Mat'l Type:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.APPROACH_SPAN_MATERIAL,
                        structure?.structureBridge?.materialAppr,
                      ),
                    },
                  ],
                  [
                    {
                      text: "5B16 Appr Span Design Type:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.APPROACH_SPAN_DESIGN,
                        structure?.structureBridge?.designAppr,
                      ),
                    },
                  ],
                  [
                    {
                      text: "5B18 Structure Length:",
                      bold: true,
                    },
                    {
                      text: structure?.structureBridge?.length,
                    },
                  ],
                  [
                    {
                      text: "VD19 Culvert Length:",
                      bold: true,
                    },
                    {
                      text: getCulvertLength(configStore, structure),
                    },
                  ],
                  [
                    {
                      text: "5B19 Deck Area:",
                      bold: true,
                    },
                    {
                      text: structure?.structureBridge?.deckArea,
                    },
                  ],
                ],
              },
              layout: "noBorders",
            },
            {
              table: {
                headerRows: 1,
                widths: ["52%", "*"],
                body: [
                  [
                    {
                      colSpan: 2,
                      text: "Classification Items",
                      fillColor: "#ebebeb",
                      alignment: "center",
                      style: ["sectionHeader"],
                    },
                    {},
                  ],
                  [
                    {
                      text: "5E01 NBIS Bridge Length:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.NBIS_BRIDGE_LENGTH,
                        structure?.structureBridge?.nbisLen,
                      ),
                    },
                  ],
                  [
                    {
                      text: "5E03 Temporary Structure:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.TEMPORARY_STRUCTURE,
                        structure?.structureBridge?.tempStruc,
                      ),
                    },
                  ],
                  [
                    {
                      text: "5E04 Historical Significance:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.HISTORICAL_SIGNIFIGANCE,
                        structure?.structureBridge?.histSign,
                      ),
                    },
                  ],
                  [
                    {
                      text: "5A24 Report Group:",
                      bold: true,
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.REPORTING_GROUP,
                        structure?.structureBridge?.reportGroup,
                      ),
                    },
                  ],
                ],
              },
              layout: "noBorders",
            },
          ],
        ],
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "2A01 Structure Notes",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
      { text: structure.structureBridge?.notes },
      {
        stack: [
          {
            table: {
              headerRows: 0,
              widths: ["100%"],
              body: [
                [
                  {
                    text: "Structure Type",
                    fillColor: "#ebebeb",
                    alignment: "center",
                    style: ["sectionHeader"],
                  },
                ],
              ],
            },
            layout: "noBorders",
          },
          {
            table: {
              headerRows: 1,
              widths: ["40%", "30%", "30%"],
              body: [
                [
                  { text: "", style: ["tableHeader"] },
                  { text: "Main", style: ["tableHeader"] },
                  { text: "Approach", style: ["tableHeader"] },
                ],
                [
                  { text: "6A26 Material" },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.MATERIAL_MAKEUP,
                      structure?.userBridge?.deptMainMaterialType,
                    ),
                  },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.MATERIAL_MAKEUP,
                      structure?.userBridge?.deptApprMaterialType,
                    ),
                  },
                ],
                [
                  { text: "6A27 Physical Span Make-Up" },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.PHYSICAL_MAKEUP,
                      structure?.userBridge?.deptMainPhysicalType,
                    ),
                  },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.PHYSICAL_MAKEUP,
                      structure?.userBridge?.deptApprPhysicalType,
                    ),
                  },
                ],
                [
                  { text: "6A28 Span Interaction" },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.SPAN_INTERACTION,
                      structure?.userBridge?.deptMainSpanInteraction,
                    ),
                  },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.SPAN_INTERACTION,
                      structure?.userBridge?.deptApprSpanInteraction,
                    ),
                  },
                ],
                [
                  { text: "6A29 Structure Type" },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.STRUCTURAL_CONFIGURATION,
                      structure?.userBridge?.deptMainStrucConfig,
                    ),
                  },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.STRUCTURAL_CONFIGURATION,
                      structure?.userBridge?.deptApprStrucConfig,
                    ),
                  },
                ],
                [
                  { text: "6A33 Wearing Surface Thickness" },
                  {
                    text: structure?.userBridge?.mainWsThickness,
                  },
                  {
                    text: structure?.userBridge?.apprWsThickness,
                  },
                ],
                [
                  { text: "6A34 WS Thickness Date" },
                  {
                    text: getFormattedDateStringNoTime(structure?.userBridge?.mainWsThickDate),
                  },
                  {
                    text: getFormattedDateStringNoTime(structure?.userBridge?.apprWsThickDate),
                  },
                ],
                [
                  { text: "6A44 FC Group No." },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.FC_GROUP_NUMBER,
                      structure?.userBridge?.mainFCGroupNum,
                    ),
                  },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.FC_GROUP_NUMBER,
                      structure?.userBridge?.apprFCGroupNum,
                    ),
                  },
                ],
                [
                  { text: "6A45 FC Member Type" },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.MEMBER_TYPE,
                      structure?.userBridge?.mainCRFMemType,
                    ),
                  },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.MEMBER_TYPE,
                      structure?.userBridge?.apprCRFMemType,
                    ),
                  },
                ],
                [
                  { text: "6A46 FC Fatigue Susceptibility" },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.CRF_FATIGUE_SUSCEPTIBILITY,
                      structure?.userBridge?.mainCRFFatigSus,
                    ),
                  },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.CRF_FATIGUE_SUSCEPTIBILITY,
                      structure?.userBridge?.apprCRFFatigSus,
                    ),
                  },
                ],
                [
                  { text: "6A47 FC Material Type" },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.CRF_MATERIAL,
                      structure?.userBridge?.mainCRFMaterial,
                    ),
                  },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.CRF_MATERIAL,
                      structure?.userBridge?.apprCRFMaterial,
                    ),
                  },
                ],
                [
                  { text: "6A48 FC ADTT" },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.CUMULATIVE_AVERAGE_DAILY_TRUCK_TRAFFIC,
                      structure?.userBridge?.mainCRFCumAdtt,
                    ),
                  },
                  {
                    text: configStore.getReferenceValue(
                      REFERENCE_TABLE.CUMULATIVE_AVERAGE_DAILY_TRUCK_TRAFFIC,
                      structure?.userBridge?.apprCRFCumAdtt,
                    ),
                  },
                ],
                [
                  { text: "6A49 FC Total CRF" },
                  {
                    text:
                      convertToInt(structure?.userBridge?.mainCRFMemType) +
                      convertToInt(structure?.userBridge?.mainCRFFatigSus) +
                      convertToInt(structure?.userBridge?.mainCRFMaterial) +
                      convertToInt(structure?.userBridge?.mainCRFCumAdtt),
                  },
                  {
                    text:
                      convertToInt(structure?.userBridge?.apprCRFMemType) +
                      convertToInt(structure?.userBridge?.apprCRFFatigSus) +
                      convertToInt(structure?.userBridge?.apprCRFMaterial) +
                      convertToInt(structure?.userBridge?.apprCRFCumAdtt),
                  },
                ],
              ],
              margin: [0, 10, 0, 0],
            },
          },
        ],
        unbreakable: true,
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Span-Specific Information",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 10, 0, 0],
      },
      {
        table: {
          headerRows: 1,
          widths: ["5%", "15%", "10%", "10%", "10%", "10%", "10%", "30%"],
          body: spanDetails,
          margin: [0, 10, 0, 0],
        },
      },
    ],
    styles: styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
  return docDefinition;
}

function getMunicipality(configStore, code) {
  const m = configStore.getMunicipalityByPlaceCode(code);
  return `${m.countyCode}/${m.municipalityNum} - ${m.municipalityName}`;
}

function getCounty(configStore, code) {
  const c = configStore.getCountyByFips(code);
  return `${c.countyCode} - ${c.countyName}`;
}

function getDistrict(configStore, code) {
  const d = configStore.getDistrictByDistrictNum(code);
  return `${d.title}`;
}

function getCulvertLength(configStore, structure) {
  let structureCulvertLength = "";
  for (let idx in structure?.culvertOpenings) {
    let structureCulvertLengthPlaceHolder = structure?.culvertOpenings[idx]?.culvertLength;
    if (idx != structure?.culvertOpenings?.length - 1 && structure?.culvertOpenings[idx]?.culvertLength) {
      structureCulvertLength += structureCulvertLengthPlaceHolder.toString() + ", ";
    } else if (structure?.culvertOpenings[idx]?.culvertLength) {
      structureCulvertLength += structureCulvertLengthPlaceHolder.toString();
    }
  }
  return structureCulvertLength;
}

function getSpecificSpanDetails(structure, specificSpans, configStore) {
  const structureUnits = structure?.structureUnits?.filter((x) => x.legacyStructureType != "X");
  for (const specificSpan of structureUnits ?? []) {
    specificSpans.push(
      [
        specificSpan.strUnitKey,
        configStore.getReferenceValue(REFERENCE_TABLE.STRUCTURE_UNIT_TYPE, specificSpan.legacyStructureType),
        specificSpan.strUnitLabel,
        specificSpan?.spanLength,
        specificSpan?.spanDeckWidth,
        {
          text: specificSpan?.spanFlared === "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
          style: "customFontAwesome",
          margin: [0, 10, 0, 0],
        },
        (specificSpan?.deptMaterialType ?? "") +
          (specificSpan?.deptPhysicalType ?? "") +
          (specificSpan?.deptSpanInteraction ?? "") +
          (specificSpan?.deptStrucConfig ?? ""),
        configStore.getDepartmentStructureTypeDescription(
          specificSpan?.deptMaterialType +
            specificSpan?.deptPhysicalType +
            specificSpan?.deptSpanInteraction +
            specificSpan?.deptStrucConfig,
        ),
      ].map((d) => {
        return {
          text: d,
          alignment: "center",
        };
      }),
    );
  }
}

function getDetailsHeader() {
  return [
    {
      text: "5D01\nUnit Key",
      style: ["tableHeader"],
    },
    {
      text: "5D04/SP01\nType",
      style: ["tableHeader"],
    },
    {
      text: "5D02/SP02\nUnit ID",
      style: ["tableHeader"],
    },
    {
      text: "SP03\nSpan Length",
      style: ["tableHeader"],
    },
    {
      text: "SP04\nDeck Width",
      style: ["tableHeader"],
    },
    {
      text: "SP05\nFlared?",
      style: ["tableHeader"],
    },
    {
      text: "SP07 to SP10\nStructural Config",
      style: ["tableHeader"],
    },
    {
      text: "Structure Type Description",
      style: ["tableHeader"],
    },
  ];
}

export { setGeneralDocDefinition };
